import organizationScheduleRepository from '@app/api/repositories/organization-schedule-repository'
import { IOrganizationSchedule } from '@app/interfaces/organization/organization-schedule'
import FormList from '@app/pages/reference-book-page-details/components/form-list/form-list'
import ModalFooter from '@app/shared/modal-footer/modal-footer'
import { openNotificationWithIcon } from '@app/shared/notification/notification'

import {
  createOrganizationSchedule,
  updateOrganizationSchedule,
} from '@app/store/actions/organization-schedule-actions'
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Spin,
  Switch,
} from 'antd'
import { AxiosResponse } from 'axios'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

interface props {
  organizationSchedule?: IOrganizationSchedule
  edit?: boolean
}

const OrganizationSchedulesForm: React.FC<props> = ({
  organizationSchedule,
  edit,
}) => {
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false)
  const [visible, setVisible] = useState<boolean>(false)
  const [form] = Form.useForm()
  const [t] = useTranslation()

  const organizationScheduleTypes = [
    'THREE_HALF_HOURS',
    'NINE_HOURS',
    'TEN_HALF_HOURS',
    'TWELVE_HOURS',
    'TWENTY_FOUR_HOURS',
    'ANOTHER',
  ]

  const showModal = () => setVisible(true)
  const hideModal = () => {
    setVisible(false)
    form.resetFields()
  }
  const dispatch = useDispatch()

  const onFinish = async (values: any) => {
    const languages = values.data.filter(
      (language) => language && language.name,
    )
    const newValues: IOrganizationSchedule = {
      id: values.id,
      classificationCode: values.classificationCode,
      sorting: values.sorting,
      status: values.status,
      data: languages,
      type: values.type,
    }
    setConfirmLoading(true)
    const organizationScheduleSubmitApi = organizationSchedule
      ? organizationScheduleRepository.update
      : organizationScheduleRepository.create

    await organizationScheduleSubmitApi(newValues)
      .then((response: AxiosResponse<IOrganizationSchedule>) => {
        if (organizationSchedule && response.status === 200) {
          setConfirmLoading(false)
          dispatch(updateOrganizationSchedule(response.data))
          form.resetFields()
          openNotificationWithIcon('success', t('success.updated'))
        }
        if (!organizationSchedule && response.status === 201) {
          setConfirmLoading(false)
          dispatch(createOrganizationSchedule(response.data))
          form.resetFields()
          openNotificationWithIcon('success', t('success.created'))
        }
        hideModal()
      })
      .catch(() => {
        setConfirmLoading(false)
        openNotificationWithIcon('error', t('errors.error'))
      })
  }

  return (
    <React.Fragment>
      <Button onClick={showModal} type={'primary'}>
        {edit ? (
          <i className="far fa-edit"> {t('general.edit')} </i>
        ) : (
          t('general.add')
        )}
      </Button>

      <Modal
        open={visible}
        title={
          edit ? t('organizationSchedule.edit') : t('organizationSchedule.add')
        }
        confirmLoading={confirmLoading}
        onCancel={hideModal}
        footer={[
          <React.Fragment key={1}>
            <ModalFooter
              confirm={confirmLoading}
              edit={edit}
              onCancel={hideModal}
              onSubmit={form.submit}
            />
          </React.Fragment>,
        ]}>
        <Spin spinning={confirmLoading}>
          <Form
            form={form}
            onFinish={onFinish}
            initialValues={{
              id: organizationSchedule?.id,
              sorting: organizationSchedule?.sorting || 0,
              status: edit ? organizationSchedule?.status : true,
              type: organizationSchedule?.type,
              classificationCode:
                organizationSchedule?.classificationCode ?? '',
            }}
            layout={'vertical'}>
            <Form.Item hidden={true} name={'id'}>
              <Input />
            </Form.Item>
            <Form.Item
              rules={[
                {
                  required: true,
                  message: t('errors.requiredMessage'),
                },
              ]}
              name={'status'}
              label={t('general.status')}
              valuePropName="checked">
              <Switch
                disabled={confirmLoading}
                defaultChecked={organizationSchedule?.status === true}
              />
            </Form.Item>
            <Form.Item
              rules={[
                {
                  required: true,
                  type: 'number',
                  min: -2147483648,
                  max: 2147483648,
                  message: t('errors.minPriorityMessage'),
                },
              ]}
              label={t('general.sort')}
              name={'sorting'}>
              <InputNumber
                disabled={confirmLoading}
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item
              rules={[
                {
                  required: true,
                  message: t('errors.requiredMessage'),
                },
              ]}
              label={t('organizationSchedule.types.type')}
              name={'type'}>
              <Select disabled={confirmLoading}>
                {organizationScheduleTypes.map(
                  (organizationScheduleType, index) => {
                    return (
                      <Select.Option
                        key={index}
                        value={organizationScheduleType}>
                        {t(
                          `organizationSchedule.types.${organizationScheduleType}`,
                        )}
                      </Select.Option>
                    )
                  },
                )}
              </Select>
            </Form.Item>
            <Form.Item
              rules={[
                {
                  required: true,
                  message: t('errors.requiredMessage'),
                },
              ]}
              label={t('organizationSchedule.classificationCode')}
              name={'classificationCode'}>
              <Input disabled={confirmLoading} style={{ width: '100%' }} />
            </Form.Item>
            <FormList
              confirmLoading={confirmLoading}
              object={organizationSchedule}
            />
          </Form>
        </Spin>
      </Modal>
    </React.Fragment>
  )
}

export default OrganizationSchedulesForm
